'use client'

import React, { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { EVENT_TYPES } from '@/collections/EventCases/constants'
import { cn } from '@/utilities/cn'
import { GOALS, reachGoal } from '@/analytics/metrika'

/** Заявка на мероприятие: падает в админку и в Telegram, как заказ. */
export const EventRequestForm: React.FC<{ className?: string }> = ({ className }) => {
  const [form, setForm] = useState({ name: '', contact: '', eventType: 'corporate', preferredDate: '', guests: '', message: '' })
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle')
  const [error, setError] = useState<{ message: string; field?: string } | null>(null)

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setState('sending')
    setError(null)
    try {
      const res = await fetch('/api/events/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, guests: form.guests ? Number(form.guests) : undefined }),
      })
      const data = (await res.json()) as { message?: string; field?: string }
      if (!res.ok) {
        setError({ message: data.message ?? 'Не получилось отправить заявку.', field: data.field })
        setState('error')
        return
      }
      reachGoal(GOALS.eventRequested)
      setState('done')
    } catch {
      setError({ message: 'Проверьте связь и попробуйте ещё раз.' })
      setState('error')
    }
  }

  if (state === 'done') {
    return (
      <div className={cn('rounded-2xl bg-sun p-6 md:p-8', className)}>
        <p className="font-display text-2xl text-ink">Заявка у нас</p>
        <p className="mt-2 text-ink/80">Свяжемся в ближайшее время и обсудим детали: площадку, дату и формат.</p>
      </div>
    )
  }

  const fieldError = (f: string) => (error?.field === f ? error.message : undefined)

  return (
    <form onSubmit={submit} className={cn('flex flex-col gap-5 rounded-2xl border-2 border-ink/15 p-6 md:p-8', className)} noValidate>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor="ev-name">Имя</Label>
          <Input id="ev-name" value={form.name} onChange={set('name')} required aria-invalid={Boolean(fieldError('name'))} />
          {fieldError('name') && <p className="text-sm text-destructive">{fieldError('name')}</p>}
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="ev-contact">Телефон или Telegram</Label>
          <Input id="ev-contact" value={form.contact} onChange={set('contact')} required placeholder="@username или +7 900 000-00-00" aria-invalid={Boolean(fieldError('contact'))} />
          {fieldError('contact') && <p className="text-sm text-destructive">{fieldError('contact')}</p>}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="flex flex-col gap-2">
          <Label htmlFor="ev-type">Формат</Label>
          <select
            id="ev-type"
            value={form.eventType}
            onChange={set('eventType')}
            className="h-11 rounded-full border-2 border-ink/15 bg-foam px-4 text-base text-ink outline-none transition-colors focus:border-ink"
          >
            {EVENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="ev-date">Когда</Label>
          <Input id="ev-date" value={form.preferredDate} onChange={set('preferredDate')} placeholder="Например, суббота в июле" />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="ev-guests">Сколько человек</Label>
          <Input id="ev-guests" type="number" inputMode="numeric" min={1} value={form.guests} onChange={set('guests')} />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="ev-message">Что хотите провести</Label>
        <Textarea id="ev-message" rows={3} value={form.message} onChange={set('message')} placeholder="Корпоратив на 20 человек с тренером и турниром" />
      </div>

      {error && !error.field && (
        <p role="alert" className="text-sm text-destructive">
          {error.message}
        </p>
      )}

      <Button type="submit" size="lg" className="self-start" disabled={state === 'sending'}>
        {state === 'sending' ? 'Отправляем…' : 'Оставить заявку'}
      </Button>
      <p className="text-xs text-ink/60">Отправляя заявку, вы соглашаетесь с обработкой персональных данных.</p>
    </form>
  )
}
